import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getPeriods, localIsoDate } from "../utils/availability";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  { type: "FULL", title: "🚫 FULL (indisponible)", hint: "Bloque toute nouvelle mission", box: "border-red-200 bg-red-50", btn: "text-red-700 bg-red-100 hover:bg-red-200" },
  { type: "ON_LEAVE", title: "🌴 EN CONGÉS", hint: "Absent, aucune mission possible", box: "border-amber-200 bg-amber-50", btn: "text-amber-800 bg-amber-100 hover:bg-amber-200" },
];

const newId = () => `new-${Math.random().toString(36).slice(2, 10)}`;

const PeriodRow = ({ period, onChange, onDelete }) => {
  const isPast = period.end_date && period.end_date < localIsoDate();
  return (
    <div className="flex items-end gap-2" data-testid={`period-row-${period.id}`}>
      <div className="flex-1">
        <label className="block text-[11px] text-gray-600 mb-0.5">Début</label>
        <input
          type="date"
          value={period.start_date}
          onChange={(e) => onChange({ ...period, start_date: e.target.value })}
          className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
          data-testid={`period-start-${period.id}`}
        />
      </div>
      <div className="flex-1">
        <label className="block text-[11px] text-gray-600 mb-0.5">Fin</label>
        <input
          type="date"
          value={period.end_date}
          min={period.start_date || undefined}
          onChange={(e) => onChange({ ...period, end_date: e.target.value })}
          className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
          data-testid={`period-end-${period.id}`}
        />
      </div>
      {isPast && <span className="text-[10px] text-gray-500 pb-1.5">Terminée</span>}
      <button
        type="button"
        onClick={onDelete}
        className="px-2 py-1 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded"
        title="Supprimer cette période"
        data-testid={`period-delete-${period.id}`}
      >
        🗑️
      </button>
    </div>
  );
};

const PeriodSection = ({ section, periods, setPeriods }) => {
  const list = periods.filter((p) => p.type === section.type);
  const update = (upd) => setPeriods((prev) => prev.map((p) => (p.id === upd.id ? upd : p)));
  const remove = (id) => setPeriods((prev) => prev.filter((p) => p.id !== id));
  const add = () => setPeriods((prev) => [...prev, { id: newId(), type: section.type, start_date: "", end_date: "" }]);
  return (
    <div className={`p-3 rounded-lg border-2 ${section.box}`} data-testid={`period-section-${section.type}`}>
      <div className="font-semibold text-gray-900">{section.title}</div>
      <div className="text-xs text-gray-600 mb-2">{section.hint}</div>
      <div className="space-y-2">
        {list.length === 0 && <div className="text-xs text-gray-500 italic">Aucune période</div>}
        {list.map((p) => (
          <PeriodRow key={p.id} period={p} onChange={update} onDelete={() => remove(p.id)} />
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className={`mt-2 px-3 py-1 rounded text-xs font-semibold ${section.btn}`}
        data-testid={`add-period-${section.type}`}
      >
        + Ajouter une période
      </button>
    </div>
  );
};

const QuotaSection = ({ quotaEnabled, setQuotaEnabled, quota, setQuota }) => (
  <div className="p-3 rounded-lg border-2 border-emerald-200 bg-emerald-50" data-testid="availability-option-AVAILABLE">
    <div className="font-semibold text-gray-900">✅ Disponible (hors périodes)</div>
    <div className="mt-2 space-y-2 text-sm">
      <label className="flex items-center gap-2">
        <input type="radio" name="quota_kind" checked={!quotaEnabled} onChange={() => setQuotaEnabled(false)} data-testid="quota-unlimited-radio" />
        <span>Illimité</span>
      </label>
      <label className="flex items-center gap-2">
        <input type="radio" name="quota_kind" checked={quotaEnabled} onChange={() => setQuotaEnabled(true)} data-testid="quota-limited-radio" />
        <span>Max</span>
        <input
          type="number"
          min="1"
          max="99"
          value={quota}
          onChange={(e) => { setQuota(e.target.value); setQuotaEnabled(true); }}
          className="w-16 px-2 py-1 border rounded text-center"
          data-testid="quota-value-input"
        />
        <span>missions / jour</span>
      </label>
    </div>
  </div>
);

const validatePeriods = (periods) => {
  for (const p of periods) {
    if (!p.start_date || !p.end_date) return "Chaque période doit avoir une date de début et une date de fin";
    if (p.end_date < p.start_date) return "La date de fin doit être ≥ à la date de début";
  }
  return null;
};

/**
 * Modal de disponibilité : quota journalier + plusieurs périodes FULL / EN CONGÉS.
 */
const EmployeeAvailabilityModal = ({ employee, onClose, onSaved }) => {
  const initialQuota = typeof employee?.daily_quota === "number" && employee.daily_quota > 0;
  const [quotaEnabled, setQuotaEnabled] = useState(initialQuota);
  const [quota, setQuota] = useState(initialQuota ? String(employee.daily_quota) : "3");
  const [periods, setPeriods] = useState(() => getPeriods(employee));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    let daily_quota = null;
    if (quotaEnabled) {
      daily_quota = parseInt(quota, 10);
      if (!daily_quota || daily_quota < 1) return toast.error("Le quota doit être un entier ≥ 1");
    }
    const err = validatePeriods(periods);
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/employees/${employee.id}/availability`, {
        availability_mode: "AVAILABLE",
        daily_quota,
        availability_start_date: initialQuota && quotaEnabled ? employee.availability_start_date || null : null,
        availability_end_date: initialQuota && quotaEnabled ? employee.availability_end_date || null : null,
        unavailability_periods: periods.map(({ id, type, start_date, end_date }) => ({
          id: id.startsWith("new-") || id === "legacy" ? null : id,
          type,
          start_date,
          end_date,
        })),
      });
      toast.success("Disponibilité mise à jour");
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const empName = `${employee?.prenom || ""} ${employee?.nom || ""}`.trim();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="availability-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Disponibilité</h3>
            <p className="text-sm text-gray-600">{empName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" data-testid="availability-close-btn">✕</button>
        </div>

        <div className="space-y-3">
          <QuotaSection quotaEnabled={quotaEnabled} setQuotaEnabled={setQuotaEnabled} quota={quota} setQuota={setQuota} />
          {SECTIONS.map((s) => (
            <PeriodSection key={s.type} section={s} periods={periods} setPeriods={setPeriods} />
          ))}
        </div>

        <div className="mt-3 text-xs text-gray-500 italic">
          En dehors des périodes FULL / EN CONGÉS, l'employé est disponible. Ce paramètre bloque uniquement
          l'attribution de nouvelles missions.
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium" data-testid="availability-cancel-btn">Annuler</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg font-medium"
            data-testid="availability-save-btn"
          >
            {saving ? "…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAvailabilityModal;
